import React, { useEffect, useState } from 'react';
import { FaCheckCircle, FaInfoCircle, FaFlask, FaStethoscope, FaDna, FaPills, FaBook, FaChevronDown,FaNotesMedical} from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

import Layout from '../../Layout';
import Loader from '../../components/CustomUI/Loader';
import { getServiceUrl } from '../../config';
import PatientBasicDetails from './PatientBasicDetails';

const apiBaseUrl = getServiceUrl();

const renderValue = (val) => (val ? val : 'N/A');

const formatContent = (content) => {
  if (!content) return '';
  
  // Check if content is already in markdown format
  const hasMarkdown = /[*_#`~]/.test(content);
  
  if (hasMarkdown) {
    return content;
  }
  
  // If not markdown, format it properly
  // Split by newlines and ensure proper spacing
  const lines = content.split('\n').filter(line => line.trim());
  
  // Format as paragraphs
  return lines.map(line => {
    // If line looks like a list item, keep it as is
    if (line.trim().startsWith('-') || line.trim().startsWith('*') || /^\d+\./.test(line.trim())) {
      return line;
    }
    // Otherwise wrap in paragraph tags
    return `<p>${line}</p>`;
  }).join('\n\n');
};

const MarkdownRenderer = ({ content }) => {
  if (!content) return <p className="text-gray-500 italic">No information available</p>;

  return (
    <div className="prose max-w-none text-justify space-y-4">
      <ReactMarkdown 
        rehypePlugins={[rehypeRaw]} 
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node, ...props }) => (
            <p {...props} className="mb-4 text-gray-600 leading-relaxed whitespace-pre-wrap" />
          ),
          ul: ({ node, ...props }) => (
            <ul {...props} className="list-disc pl-4 mb-4 space-y-2" />
          ),
          ol: ({ node, ...props }) => (
            <ol {...props} className="list-decimal pl-4 mb-4 space-y-2" />
          ),
          li: ({ node, ...props }) => (
            <li {...props} className="text-gray-600" />
          ),
          pre: ({ node, ...props }) => (
            <pre {...props} className="whitespace-pre-wrap break-words" />
          )
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

const InfoSection = ({ title, content, isOpen, onToggle }) => (
  <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
    <button
      onClick={onToggle}
      className="w-full px-8 py-6 flex items-center justify-between text-left cursor-pointer"
    >
      <h3 className="text-primary text-lg font-semibold flex items-center">
        <span className="w-1 h-6 bg-primary rounded-full mr-4"></span>
        {title}
      </h3>
      <FaChevronDown className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`} />
    </button>
    {isOpen && (
      <div className="px-8 pb-8">
        <div className="pl-5 border-l-2 border-gray-100 space-y-4">
          <div className="prose max-w-none text-gray-600 leading-relaxed">
            <MarkdownRenderer content={content} />
          </div>
        </div>
      </div>
    )}
  </div>
);

const TabIcon = ({ tab }) => {
  const iconBaseClass = "w-10 h-6"; // smaller and consistent size
  switch (tab) {
    case 'overview':
      return <FaInfoCircle className={`${iconBaseClass} text-blue-500`} />;
    case 'details':
      return <FaFlask className={`${iconBaseClass} text-purple-500`} />;
    case 'diagnosis':
      return <FaStethoscope className={`${iconBaseClass} text-red-500`} />;
    case 'management':
      return <FaNotesMedical className={`${iconBaseClass} text-pink-500`} />;
    case 'genetics':
      return <FaDna className={`${iconBaseClass} text-green-600`} />;
    case 'drugs':
      return <FaPills className={`${iconBaseClass} text-yellow-500`} />;
    case 'source':
      return <FaBook className={`${iconBaseClass} text-gray-600`} />;
    default:
      return null;
  }
};

export default function DiseaseDetails() {
  const patientData = localStorage.getItem('patientData')
    ? JSON.parse(localStorage.getItem('patientData'))
    : [];

  const { patientguid, diseaseName } = useParams();

  const [diseaseDetails, setDiseaseDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [noDetailsFound, setNoDetailsFound] = useState(false);
  const [selectedDisease, setSelectedDisease] = useState(false);
  const [openSections, setOpenSections] = useState({});

  useEffect(() => {
    const fetchDiseaseDetails = async () => {
      if (!patientguid || !diseaseName) {
        console.error('Missing patientguid or diseaseName in URL parameters.');
        return;
      }

      const formBody = { name: diseaseName };
      const config = {
        headers: {
          rdxtoken: localStorage.getItem('tokenId'),
        },
      };

      try {
        const response = await axios.post(
          apiBaseUrl + 'doctors/diseaseinfo',
          formBody,
          config
        );

        const data = response.data?.data || response.data[0];

        if (!data) {
          setNoDetailsFound(true);
          setLoading(false);
          return;
        }

        try {
          data.hpo_terms = JSON.parse(data.hpo_terms);
        } catch {
          data.hpo_terms = [];
        }

        setDiseaseDetails(data);
        setNoDetailsFound(false);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching disease details:', error);
        setLoading(false);
      }
    };

    fetchDiseaseDetails();
  }, [patientguid, diseaseName]);

  const handleSelectedDisease = () => {
    setSelectedDisease(!selectedDisease);
  };

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Reset open sections when changing tabs
  useEffect(() => {
    setOpenSections({});
  }, [activeTab]);

  return (
    <Layout patientguid={patientguid} diseaseName={diseaseName}>
      {patientData && <PatientBasicDetails patientData={patientData} />}
      {loading ? (
        <div className="flex justify-center items-center min-h-[400px]">
          <Loader />
        </div>
      ) : (
        <div className="my-8 rounded-xl border border-gray-200 bg-white shadow-lg">
          {noDetailsFound ? (
            <div className="py-16 text-center">
              <div className="text-gray-400 text-6xl mb-4">
                <FaInfoCircle className="inline-block" />
              </div>
              <div className="text-gray-600 text-xl mb-2">No details found</div>
              <div className="text-gray-400">No information available for {diseaseName.toUpperCase()}</div>
            </div>
          ) : (
            <>
              <div className="border-b border-gray-200 bg-white px-6 py-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h2 className="text-primary text-2xl font-bold mb-1">
                      {diseaseName.toUpperCase()}
                    </h2>
                    <p className="text-gray-500 text-sm">Disease Information Details</p>
                  </div>
                  <button
                    className={`px-6 py-2.5 ${
                      selectedDisease ? 'bg-primary' : 'bg-gray-500'
                    } flex items-center rounded-lg text-white transition-all duration-200 hover:shadow-md`}
                    onClick={handleSelectedDisease}
                    disabled={selectedDisease}
                  >
                    {selectedDisease ? (
                      <>
                        <FaCheckCircle className="mr-2" />
                        Selected Disease
                      </>
                    ) : (
                      'SELECT THIS DISEASE'
                    )}
                  </button>
                </div>
              </div>

              <div className="border-b border-gray-200 bg-white px-6">
                <div className="flex overflow-x-auto">
                  {[
                    'overview',
                    'details',
                    'diagnosis',
                    'management',
                    'genetics',
                    'drugs',
                    'source',
                  ].map((tab) => (
                    <button
                      key={tab}
                      className={`px-6 py-4 text-sm font-medium capitalize flex items-center transition-all duration-200 relative ${
                        activeTab === tab 
                          ? 'text-primary border-b-2 border-primary bg-blue-50/50' 
                          : 'text-gray-600 hover:text-primary hover:bg-gray-50'
                      }`}
                      onClick={() => setActiveTab(tab)}
                    >
                      <TabIcon tab={tab} />
                      <span className="ml-2">{tab.replace('_', ' ')}</span>
                      {activeTab === tab && (
                        <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary"></span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-6 bg-gray-50">
                <div className="max-w-5xl mx-auto">
                  {activeTab === 'overview' && (
                    <>
                      <InfoSection
                        title="Clinical Description"
                        content={diseaseDetails.clinical_description}
                        isOpen={openSections['clinical_description']}
                        onToggle={() => toggleSection('clinical_description')}
                      />
                    </>
                  )}

                  {activeTab === 'details' && (
                    <div className="grid gap-6">
                      <InfoSection
                        title="Disease Category"
                        content={diseaseDetails.disease_category}
                        isOpen={openSections['disease_category']}
                        onToggle={() => toggleSection('disease_category')}
                      />
                      <InfoSection
                        title="Epidemiology"
                        content={diseaseDetails.epidemiology}
                        isOpen={openSections['epidemiology']}
                        onToggle={() => toggleSection('epidemiology')}
                      />
                      <InfoSection
                        title="Etiology"
                        content={diseaseDetails.etiology}
                        isOpen={openSections['etiology']}
                        onToggle={() => toggleSection('etiology')}
                      />
                    </div>
                  )}

                  {activeTab === 'diagnosis' && (
                    <div className="grid gap-6">
                      <InfoSection
                        title="Diagnostic Methods"
                        content={diseaseDetails.diagnostic_methods}
                        isOpen={openSections['tests_order']}
                        onToggle={() => toggleSection('tests_order')}
                      />
                      <InfoSection
                        title="Differential Diagnosis"
                        content={diseaseDetails.differential_diagnosis}
                        isOpen={openSections['differential_diagnosis']}
                        onToggle={() => toggleSection('differential_diagnosis')}
                      />
                      <InfoSection
                        title="Antenatal Diagnosis"
                        content={diseaseDetails.antenatal_diagnosis}
                        isOpen={openSections['antenatal_diagnosis']}
                        onToggle={() => toggleSection('antenatal_diagnosis')}
                      />
                    </div>
                  )}

                  {activeTab === 'management' && (
                    <div className="grid gap-6">
                      <InfoSection
                        title="Management & Treatment"
                        content={diseaseDetails.management_and_treatment}
                        isOpen={openSections['management_treatment']}
                        onToggle={() => toggleSection('management_treatment')}
                      />
                      <InfoSection
                        title="Prognosis"
                        content={diseaseDetails.prognosis}
                        isOpen={openSections['prognosis']}
                        onToggle={() => toggleSection('prognosis')}
                      />
                      <InfoSection
                        title="Genetic Counseling"
                        content={diseaseDetails.genetic_counseling}
                        isOpen={openSections['genetic_counseling']}
                        onToggle={() => toggleSection('genetic_counseling')}
                      />
                    </div>
                  )}

                  {activeTab === 'genetics' && (
                    <div className="grid gap-6">
                      <InfoSection
                        title="Protein Name"
                        content={diseaseDetails.protein_name}
                        isOpen={openSections['protein_name']}
                        onToggle={() => toggleSection('protein_name')}
                      />
                      <InfoSection
                        title="Mutation"
                        content={diseaseDetails.mutation}
                        isOpen={openSections['mutation']}
                        onToggle={() => toggleSection('mutation')}
                      />
                      <InfoSection
                        title="Mutation Type"
                        content={diseaseDetails.mutation_type}
                        isOpen={openSections['mutation_type']}
                        onToggle={() => toggleSection('mutation_type')}
                      />
                      <InfoSection
                        title="Protein Change"
                        content={diseaseDetails.protein_change}
                        isOpen={openSections['protein_change']}
                        onToggle={() => toggleSection('protein_change')}
                      />
                    </div>
                  )}

                  {activeTab === 'drugs' && (
                    <div className="grid gap-6">
                      <InfoSection
                        title="Indication Drugs"
                        content={diseaseDetails.indication_drugs}
                        isOpen={openSections['indication_drugs']}
                        onToggle={() => toggleSection('indication_drugs')}
                      />
                      <InfoSection
                        title="Contraindication Drugs"
                        content={diseaseDetails.contraindication_drugs}
                        isOpen={openSections['contraindication_drugs']}
                        onToggle={() => toggleSection('contraindication_drugs')}
                      />
                      <InfoSection
                        title="Off-label Use"
                        content={diseaseDetails.off_label_use}
                        isOpen={openSections['off_label_use']}
                        onToggle={() => toggleSection('off_label_use')}
                      />
                      <InfoSection
                        title="Pathway"
                        content={diseaseDetails.pathway}
                        isOpen={openSections['pathway']}
                        onToggle={() => toggleSection('pathway')}
                      />
                      <InfoSection
                        title="Exposure"
                        content={diseaseDetails.exposure}
                        isOpen={openSections['exposure']}
                        onToggle={() => toggleSection('exposure')}
                      />
                    </div>
                  )}

                  {activeTab === 'source' && (
                    <InfoSection
                      title="Source of Information"
                      content={diseaseDetails.source}
                      isOpen={openSections['source']}
                      onToggle={() => toggleSection('source')}
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </Layout>
  );
}
